import { useState, useEffect } from "react";
import { userService, User } from "../../services/userService";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";
import { useAuth } from "../../contexts/AuthContext";
import { PermissionsSelector } from "./PermissionsSelector";
import { Permission } from "../../types";
import { PhoneInput, isValidE164Phone } from "../ui/PhoneInput";

interface EditUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
  allowedRoles?: string[];
  canChangeRole?: boolean;
  canEditPermissions?: boolean;
}

export function EditUserDialog({
  isOpen,
  onClose,
  onSuccess,
  user,
  allowedRoles = ["student", "instructor", "admin"],
  canChangeRole = false,
  canEditPermissions = false,
}: EditUserDialogProps) {
  const { user: currentUser, isSuperAdmin: currentUserIsSuperAdmin } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    role: "student",
    deviceLimit: 0,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  const isEditingSelf = user?._id === currentUser?._id;
  const canEditPermissionsForUser = canEditPermissions && !isEditingSelf;
  const canEditSuperAdminStatus = currentUserIsSuperAdmin && !isEditingSelf;

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        role: user.role || "student",
        deviceLimit: user.deviceLimit || 0,
      });
      setSelectedPermissions(user.permissions || []);
      setIsSuperAdmin(user.isSuperAdmin || false);
      setPhoneNumber(user.phoneNumber || "");
      setPhoneError(null);
    }
  }, [user, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Phone is optional. If a value was typed, it must be a complete E.164
    // number (server is authoritative and re-validates too).
    if (phoneNumber && !isValidE164Phone(phoneNumber)) {
      setPhoneError("Please enter a complete phone number");
      return;
    }
    setPhoneError(null);

    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const updateData: any = {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        deviceLimit: Number(formData.deviceLimit),
      };

      // Always send phoneNumber, including empty string, so the backend
      // can clear an existing value when the admin deletes it.
      updateData.phoneNumber = phoneNumber || "";

      if (canChangeRole) {
        updateData.role = formData.role;
      }

      if (user.role === "admin" && canEditPermissionsForUser) {
        updateData.permissions = isSuperAdmin ? [] : selectedPermissions;
        updateData.isSuperAdmin = isSuperAdmin;
      }

      await userService.updateUser(user._id, updateData);
      showSuccess("User updated successfully", "Success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Update user error:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(apiError.message, "Update Failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit User">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            <p className="font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <Input
              id="firstName"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <Input
              id="lastName"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="deviceLimit"
            className="text-sm font-medium text-gray-700"
          >
            Device Limit
          </label>
          <div className="flex flex-col gap-1">
            <Input
              id="deviceLimit"
              type="number"
              min="0"
              placeholder="0"
              value={formData.deviceLimit}
              onChange={handleChange}
            />
            <span className="text-xs text-gray-500">
              Set to 0 to use the global default limit.
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username"
            className="text-sm font-medium text-gray-700"
          >
            Username
          </label>
          <Input
            id="username"
            placeholder="Choose a username"
            value={formData.username}
            onChange={handleChange}
            required
            autoComplete="username"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={formData.email}
            onChange={handleChange}
            required
            disabled // Email is usually not editable this way
            className="bg-gray-50 opacity-70"
            autoComplete="email"
          />
          <p className="text-[10px] text-gray-400">
            Email cannot be changed from this dialog.
          </p>
        </div>

        <div className="space-y-2">
          <PhoneInput
            value={phoneNumber}
            onChange={(e164) => {
              setPhoneNumber(e164);
              if (phoneError) setPhoneError(null);
            }}
            label="Phone Number"
            placeholder="50 123 4567"
            optional
            error={phoneError}
            helperText="Include country code (e.g., +971 50 123 4567)"
          />
        </div>

        {canChangeRole && (
          <div className="space-y-2">
            <label htmlFor="role" className="text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
            >
              {allowedRoles.map((role) => (
                <option key={role} value={role} className="capitalize">
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Admin permission display/edit */}
        {user?.role === "admin" && (
          <>
            {isEditingSelf && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  You cannot modify your own permissions or super admin status.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {canEditSuperAdminStatus && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="isSuperAdmin"
                    checked={isSuperAdmin}
                    onChange={(e) => {
                      setIsSuperAdmin(e.target.checked);
                      if (e.target.checked) {
                        setSelectedPermissions([]);
                      }
                    }}
                    disabled={isEditingSelf}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <label
                      htmlFor="isSuperAdmin"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      Super Admin
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Super admins have all permissions and can manage other admins.
                    </p>
                  </div>
                </div>
              )}

              {canEditPermissionsForUser && !isSuperAdmin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="max-h-64 overflow-y-auto">
                    <PermissionsSelector
                      selectedPermissions={selectedPermissions}
                      onChange={setSelectedPermissions}
                      compact
                    />
                  </div>
                </div>
              )}

              {!canEditPermissionsForUser && !isSuperAdmin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    {user.permissions?.length || 0} permissions assigned
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
